"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useReducedMotion } from "motion/react";

type MagneticButtonProps = {
  children: ReactNode;
  strength?: number;
  className?: string;
  href?: string;
  onClick?: () => void;
  ariaLabel?: string;
};

export default function MagneticButton({
  children,
  strength = 0.3,
  className = "",
  href,
  onClick,
  ariaLabel,
}: MagneticButtonProps) {
  const wrapperRef = useRef<HTMLElement | null>(null);
  const innerRef = useRef<HTMLSpanElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const reduce = useReducedMotion();
  const [isCoarse, setIsCoarse] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setIsCoarse(window.matchMedia("(pointer: coarse)").matches);
  }, []);

  useEffect(() => {
    if (reduce || isCoarse) return;
    const wrapper = wrapperRef.current;
    const inner = innerRef.current;
    if (!wrapper || !inner) return;

    const handleMove = (e: PointerEvent) => {
      const rect = wrapper.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) * strength;
      const dy = (e.clientY - cy) * strength;
      const clampedX = Math.max(-12, Math.min(12, dx));
      const clampedY = Math.max(-12, Math.min(12, dy));
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        inner.style.transform = `translate(${clampedX}px, ${clampedY}px)`;
      });
    };

    const handleLeave = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        inner.style.transform = "translate(0px, 0px)";
      });
    };

    wrapper.addEventListener("pointermove", handleMove);
    wrapper.addEventListener("pointerleave", handleLeave);
    return () => {
      wrapper.removeEventListener("pointermove", handleMove);
      wrapper.removeEventListener("pointerleave", handleLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [strength, reduce, isCoarse]);

  const inner = (
    <span
      ref={innerRef}
      className="inline-block will-change-transform"
      style={{ transition: "transform 300ms cubic-bezier(0.16,1,0.3,1)" }}
    >
      {children}
    </span>
  );

  if (reduce || isCoarse) {
    if (href) {
      return (
        <a href={href} className={className} aria-label={ariaLabel} onClick={onClick}>
          {children}
        </a>
      );
    }
    return (
      <button type="button" className={className} aria-label={ariaLabel} onClick={onClick}>
        {children}
      </button>
    );
  }

  if (href) {
    return (
      <a
        ref={wrapperRef as React.RefObject<HTMLAnchorElement>}
        href={href}
        className={className}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      ref={wrapperRef as React.RefObject<HTMLButtonElement>}
      type="button"
      className={className}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {inner}
    </button>
  );
}
